import {
  CustomCTAVisualPreview,
  DefaultCTAVisualPreview,
} from "@jugl-web/domain-resources/chat-bot";
import { useCTAVisualSource } from "@jugl-web/domain-resources/chat-bot/hooks/chatBotMediaSourceResolvers";
import { defaultCTAVisualList } from "@jugl-web/domain-resources/chat-bot/utils/defaultCTAVisuals/defaultCTAVisuals";
import { isDefaultCTAVisual } from "@jugl-web/domain-resources/chat-bot/utils/isDefaultCTAVisual";
import { useFileSelect } from "@jugl-web/domain-resources/files/hooks/useFileSelect";
import { InteractiveContainer } from "@jugl-web/ui-kit";
import {
  cx,
  isFileTypeAccepted,
  useToast,
  useTranslations,
} from "@jugl-web/utils";
import { getVideoDuration } from "@jugl-web/utils/utils/files";
import partition from "lodash/partition";
import { FC, ReactNode, useCallback, useMemo, useState } from "react";
import { Separator } from "../Separator";
import { TabHeader } from "../TabHeader";
import { EditableMediaContainer } from "../EditableMediaContainer";
import { InfoIndicator } from "../InfoIndicator";

import { ReactComponent as NoImageIcon } from "./assets/no-image.svg";
import { ReactComponent as UploadIcon } from "./assets/upload.svg";

export type CTAVisualValue = string | File | null;

type CTAVisualSectionProps = {
  value: CTAVisualValue;
  onChange: (value: CTAVisualValue) => void;
};

const CTA_VISUAL_ACCEPT_TYPES =
  "image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm";

const MAX_IMAGE_BYTES = 1 * 1024 * 1024;
const MAX_CLIP_BYTES = 3 * 1024 * 1024;
const MAX_CLIP_DURATION_SEC = 15.999;

export const CTAVisualSection: FC<CTAVisualSectionProps> = ({
  value,
  onChange,
}) => {
  const [previousCustomVisual, setPreviousCustomVisual] =
    useState<CTAVisualValue>(null);

  const { selectFile } = useFileSelect();
  const { t } = useTranslations();
  const { toast } = useToast();

  const customVisualSrc = useCTAVisualSource(
    value !== null && !isDefaultCTAVisual(value) ? value : null
  );

  const previousCustomVisualSrc = useCTAVisualSource(previousCustomVisual);

  const canRestorePreviousCustomVisual =
    previousCustomVisual !== null && customVisualSrc === null;

  const hasCustomOrPreviousPreview =
    customVisualSrc !== null || previousCustomVisualSrc !== null;

  const handleFileChange = useCallback(async () => {
    const selectedFiles = await selectFile({
      acceptTypes: CTA_VISUAL_ACCEPT_TYPES,
      maxFileSize: MAX_CLIP_BYTES,
      multiple: false,
    });

    const file = selectedFiles?.[0];
    if (!file) return;

    if (!isFileTypeAccepted(file, CTA_VISUAL_ACCEPT_TYPES)) {
      toast(
        t({
          id: "client-support-page.incorrect-file-format",
          defaultMessage: "Incorrect file format ❌",
        }),
        { variant: "error" }
      );
      return;
    }

    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");

    if (!isImage && !isVideo) {
      toast(
        t({
          id: "client-support-page.incorrect-file-format",
          defaultMessage: "Incorrect file format ❌",
        }),
        { variant: "error" }
      );
      return;
    }

    if (isImage && file.size > MAX_IMAGE_BYTES) {
      toast(
        t(
          {
            id: "client-support-page.image-too-large",
            defaultMessage: "Image must be up to {maxMb} MB",
          },
          { maxMb: 1 }
        ),
        { variant: "error" }
      );
      return;
    }

    if (isVideo) {
      if (file.size > MAX_CLIP_BYTES) {
        toast(
          t(
            {
              id: "client-support-page.clip-too-large",
              defaultMessage: "Clip must be up to {maxMb} MB",
            },
            { maxMb: 3 }
          ),
          { variant: "error" }
        );
        return;
      }

      let duration = 0;
      try {
        duration = await getVideoDuration(file);
      } catch {
        // Continue with 0 duration if metadata cannot be read
      }

      if (duration > MAX_CLIP_DURATION_SEC) {
        toast(
          t(
            {
              id: "client-support-page.clip-too-long",
              defaultMessage: "Clip must be up to {maxSec} seconds",
            },
            { maxSec: Math.floor(MAX_CLIP_DURATION_SEC) }
          ),
          { variant: "error" }
        );
        return;
      }
    }

    onChange(file);
  }, [selectFile, toast, t, onChange]);

  const handleSelectDefaultVisual = useCallback(
    (id: string) => {
      if (value !== null && !isDefaultCTAVisual(value)) {
        setPreviousCustomVisual(value);
      }

      onChange(id);
    },
    [value, onChange]
  );

  const handleSelectNoVisual = useCallback(() => {
    if (value !== null && !isDefaultCTAVisual(value)) {
      setPreviousCustomVisual(value);
    }

    onChange(null);
  }, [value, onChange]);

  const handleRemoveCustomVisual = useCallback(() => {
    if (value !== null && !isDefaultCTAVisual(value)) {
      onChange(null);
    }

    setPreviousCustomVisual(null);
  }, [value, onChange]);

  const handleRestorePreviousCustomVisual = useCallback(() => {
    if (previousCustomVisual === null) return;
    onChange(previousCustomVisual);
  }, [previousCustomVisual, onChange]);

  const [defaultRecordings, defaultAnimations] = useMemo(
    () =>
      partition(defaultCTAVisualList, (visual) =>
        visual.id.startsWith("default-recording")
      ),
    []
  );

  const resolvedCustomPreviewSrc = customVisualSrc ?? previousCustomVisualSrc;

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <TabHeader
          title={t({
            id: "client-support-page.include-visual",
            defaultMessage: "Include Visual",
          })}
          subtitle={t({
            id: "client-support-page.include-visuals-subtitle",
            defaultMessage:
              "Pick a preset, upload your own image or clip, or hide the visual",
          })}
        />

        <InfoIndicator
          tooltipPlacement="top"
          message={
            <ul className="m-0 flex max-w-[340px] list-disc flex-col gap-1 pl-4 text-left text-sm">
              <li>
                {t(
                  {
                    id: "client-support-page.upload-hint-clip",
                    defaultMessage:
                      "<b>Clip:</b> Upload MP4, GIF, or WEBM (max 3 MB, up to 15 sec)",
                  },
                  {
                    b: (chunks: ReactNode) => (
                      <span className="font-semibold">{chunks}</span>
                    ),
                  }
                )}
              </li>
              <li>
                {t(
                  {
                    id: "client-support-page.upload-hint-image",
                    defaultMessage:
                      "<b>Image:</b> Upload JPG, PNG, or WEBP (max 1 MB)",
                  },
                  {
                    b: (chunks: ReactNode) => (
                      <span className="font-semibold">{chunks}</span>
                    ),
                  }
                )}
              </li>
            </ul>
          }
        />
      </div>

      <div className="grid w-full grid-cols-3 gap-6">
        {/* No visual tile */}
        <TileContainer
          isSelected={value === null}
          className="flex items-center justify-center"
          onClick={handleSelectNoVisual}
        >
          <NoImageIcon />
        </TileContainer>

        {/* Custom visual tile */}
        {hasCustomOrPreviousPreview ? (
          <EditableMediaContainer
            onSelectNew={handleFileChange}
            onRemove={handleRemoveCustomVisual}
          >
            <TileContainer
              isSelected={customVisualSrc !== null}
              isDisabled={customVisualSrc !== null}
              onClick={
                canRestorePreviousCustomVisual
                  ? handleRestorePreviousCustomVisual
                  : () => undefined
              }
            >
              <div className="relative h-full w-full">
                {resolvedCustomPreviewSrc !== null ? (
                  <CustomCTAVisualPreview
                    src={resolvedCustomPreviewSrc}
                    fallback={<UploadIcon />}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <UploadIcon />
                )}
              </div>
            </TileContainer>
          </EditableMediaContainer>
        ) : (
          <TileContainer
            className="flex items-center justify-center"
            onClick={handleFileChange}
          >
            <UploadIcon />
          </TileContainer>
        )}

        {/* Realistic recordings */}
        {defaultRecordings.map((visual) => (
          <TileContainer
            key={visual.id}
            isSelected={typeof value === "string" && value === visual.id}
            onClick={() => handleSelectDefaultVisual(visual.id)}
          >
            <DefaultCTAVisualPreview id={visual.id} src={visual.src} />
          </TileContainer>
        ))}
      </div>

      <Separator />
      {/* Bottom visuals grid */}
      <div className="grid w-full grid-cols-3 gap-6">
        {/* Emoji-like animations */}
        {defaultAnimations.map((visual) => (
          <TileContainer
            key={visual.id}
            isSelected={typeof value === "string" && value === visual.id}
            onClick={() => handleSelectDefaultVisual(visual.id)}
          >
            <DefaultCTAVisualPreview id={visual.id} src={visual.src} />
          </TileContainer>
        ))}
      </div>
    </div>
  );
};

interface TileContainerProps {
  children: ReactNode;
  isSelected?: boolean;
  isDisabled?: boolean;
  className?: string;
  onClick: () => void;
}

const TileContainer: FC<TileContainerProps> = ({
  children,
  isSelected = false,
  isDisabled = false,
  className,
  onClick,
}) => {
  return (
    <InteractiveContainer
      onClick={onClick}
      isDisabled={isDisabled}
      className={cx(
        "relative flex h-[78px] items-center justify-center overflow-hidden rounded-xl border border-solid border-[#F2F2F2] bg-[#F9F9F9] outline outline-2 outline-transparent transition-all",
        !isDisabled && !isSelected && "hover:outline-dark-500",
        isSelected && "outline-dark",
        className
      )}
    >
      {children}
    </InteractiveContainer>
  );
};
